// DJB-209: admin-side catalogue of the OptionalFeature enum that DJB-Server
// PR #62 aggregates (GET /optional-features/aggregation) and bulk-applies
// (PUT /optional-features/bulk).
//
// - `key` is the enum constant on the wire (SHUFFLE_TRACKS_AMOUNT, FREE_PLAY…).
// - option `value`s are the textual sub-options the aggregation reports and
//   the bulk endpoint parses: booleans as "true"/"false", numbers in decimal,
//   Party Shuffle's 0 (or lower) as "OFF", a missing string as "".
// - `kind` drives the two visual treatments the ticket asks for: 'toggle' is
//   a plain ON/OFF switch, 'choice' has several sub-options. Either way a
//   machine holds exactly ONE value per feature — applying a value overwrites
//   whatever the machine had before.
// - labels and option sets mirror the per-machine form in MachineDetails, so
//   the fleet view and the single-machine view speak the same language.
// - `hidden` marks features the backend aggregates but the admin UI does not
//   expose: the DJB-87 not-in-use ones, like the per-machine form hides them,
//   and LONG_TRACK_PRICE (DJB-223), whose pricing is managed centrally via the
//   Long Track config-model + Config Subscriptions rather than as a bulk toggle.
export const FEATURE_KINDS = Object.freeze({ TOGGLE: 'toggle', CHOICE: 'choice' });

const onOff = () => [
  { value: 'false', label: 'OFF' },
  { value: 'true', label: 'ON' },
];

export const OPTIONAL_FEATURE_CATALOG = [
  // ---- ON / OFF features -------------------------------------------------
  { key: 'FREE_PLAY', label: 'Free Play', kind: FEATURE_KINDS.TOGGLE, options: onOff() },
  { key: 'LIVE_DELETE', label: 'Live deletes', kind: FEATURE_KINDS.TOGGLE, options: onOff() },

  // ---- Multi-option features ---------------------------------------------
  {
    key: 'SHUFFLE_TRACKS_AMOUNT',
    label: 'Party Shuffle',
    kind: FEATURE_KINDS.CHOICE,
    // 0 = OFF on the wire; the aggregation already reports it as "OFF"
    options: [
      { value: 'OFF', label: 'OFF' },
      { value: '6', label: '6' },
      { value: '8', label: '8' },
      { value: '10', label: '10' },
    ],
  },
  {
    key: 'GIFT_CREDIT_AFTER',
    label: 'Credit Gift',
    kind: FEATURE_KINDS.CHOICE,
    options: [
      { value: '0', label: 'NEVER' },
      { value: '30', label: '30 minutes' },
      { value: '60', label: '60 minutes' },
      { value: '90', label: '90 minutes' },
      { value: '120', label: '120 minutes' },
    ],
  },
  {
    key: 'SKIP_QUEUE_CREDITS',
    label: 'Skip queue',
    kind: FEATURE_KINDS.CHOICE,
    options: [
      { value: '-1', label: 'OFF' },
      { value: '0', label: 'FREE' },
      { value: '1', label: '1 Credit' },
      { value: '2', label: '2 Credits' },
      { value: '3', label: '3 Credits' },
      { value: '5', label: '5 Credits' },
    ],
  },
  {
    key: 'CREDITS_PER_TOKEN',
    label: 'Credits per token',
    kind: FEATURE_KINDS.CHOICE,
    options: [
      { value: '1', label: '1c / Token' },
      { value: '2', label: '2c / Token' },
      { value: '3', label: '3c / Token' },
      { value: '5', label: '5c / Token' },
      { value: '10', label: '10c / Token' },
    ],
  },
  {
    key: 'QUICK_CHECKOUT',
    label: 'Mobile Pay checkout',
    kind: FEATURE_KINDS.CHOICE,
    options: [
      { value: 'OFF', label: 'OFF' },
      { value: '50-40-40', label: '50-40-40' },
    ],
  },
  {
    key: 'SEARCH_STYLE',
    label: 'Search style',
    kind: FEATURE_KINDS.CHOICE,
    options: [
      { value: 'LEGACY', label: 'LEGACY' },
      { value: 'HYBRID', label: 'HYBRID' },
      { value: 'NEW', label: 'NEW' },
      { value: 'NEW_STYLE', label: 'NEW_STYLE' },
    ],
  },
  {
    key: 'DEFAULT_CATEGORY_VIEW',
    label: 'Default category view',
    kind: FEATURE_KINDS.CHOICE,
    options: [
      { value: 'LISTS', label: 'LISTS' },
      { value: 'CATEGORIES', label: 'CATEGORIES' },
    ],
  },

  // ---- Aggregated by the backend, not exposed in the admin UI -------------
  // DJB-223: long-track pricing lives in the Long Track config-model + Config
  // Subscriptions, so it is not offered as a bulk optional feature.
  {
    key: 'LONG_TRACK_PRICE',
    label: 'Long Track Price',
    kind: FEATURE_KINDS.TOGGLE,
    hidden: true,
    options: onOff(),
  },
  // not in use yet (DJB-87)
  {
    key: 'SCREENSAVER_QUIZ_CREDITS',
    label: 'Screensaver quiz',
    kind: FEATURE_KINDS.CHOICE,
    hidden: true,
    options: [
      { value: '-1', label: 'OFF' },
      { value: '0', label: 'No credit win' },
      { value: '1', label: 'Credit on correct answer' },
    ],
  },
  {
    key: 'MOBILE_HAPPY_HOUR',
    label: 'Mobile happy hour',
    kind: FEATURE_KINDS.CHOICE,
    hidden: true,
    options: [{ value: '', label: 'OFF' }],
  },
];

export const VISIBLE_FEATURES = OPTIONAL_FEATURE_CATALOG.filter((f) => !f.hidden);

export const featureByKey = (key) => OPTIONAL_FEATURE_CATALOG.find((f) => f.key === key) ?? null;

// Label for a sub-option value; unlisted values fall back to the raw text,
// and a missing string value ("") reads as "Not set".
export const optionLabel = (feature, value) => {
  const known = feature?.options?.find((o) => o.value === value);
  if (known) return known.label;
  return value === '' || value == null ? 'Not set' : String(value);
};

// Merges a feature's catalogue options with the aggregation the backend
// returned for it: every catalogue option in catalogue order (count 0 when no
// machine sits on it), followed by any value the fleet holds that the
// catalogue does not list (`listed: false`), in the backend's order — so a
// stray value never disappears from the counts.
export const mergeSubOptionCounts = (feature, aggregationEntry) => {
  const counts = new Map();
  (aggregationEntry?.values ?? []).forEach(({ value, count }) => {
    counts.set(String(value ?? ''), Number(count) || 0);
  });
  const rows = feature.options.map((option) => ({
    value: option.value,
    label: option.label,
    count: counts.get(option.value) ?? 0,
    listed: true,
  }));
  const listed = new Set(feature.options.map((o) => o.value));
  counts.forEach((count, value) => {
    if (!listed.has(value)) {
      rows.push({ value, label: optionLabel(feature, value), count, listed: false });
    }
  });
  return rows;
};
